import { apiClient } from "@/api/client";
import { AxiosError } from "axios";
import { create } from "zustand";

const ACTIVE_AGENCY_STORAGE_KEY = "activeAgencyId";

interface AuthState {
  user: User | null;
  user_permissions: string[] | null;
  user_role: string | null;
  sidebar: any[] | null;
  authToken: string | null;
  media_agency_information: MediaAgencyInformation | null;
  /**
   * The currently active agency for the session. Sent on every authenticated
   * request via the `X-Agency-Id` header (see `api/client.ts`).
   */
  activeAgencyId: string | null;
  /**
   * The list of agencies the current user is a member of, populated when the
   * login response includes `requires_agency_selection` or refreshed via
   * `loadAgencies()`.
   */
  agencies: AgencyMembership[];
  /**
   * Whether the most recent login response asked the user to pick an agency
   * before entering the app. Used by the login screen to render the selector.
   */
  requiresAgencySelection: boolean;
  setUser: (user: User) => void;
  setAuthToken: (token: string) => void;
  setAuth: (
    user: User,
    token: string,
    user_permissions: string[],
    user_role: string,
    sidebar: any[],
    media_agency_information: MediaAgencyInformation
  ) => void;
  /**
   * Update media agency information only
   */
  setMediaAgencyInformation: (media_agency_information: MediaAgencyInformation) => void;
  /**
   * Update the active agency id. Persists to localStorage so it survives
   * reloads, and clears it when `null` is passed.
   */
  setActiveAgency: (agencyId: string | null) => void;
  /**
   * Replace the cached list of agencies the user belongs to.
   */
  setAgencies: (agencies: AgencyMembership[]) => void;
  /**
   * Fetch the current user's agencies from the API and cache them in the store.
   */
  loadAgencies: () => Promise<AgencyMembership[]>;
  /**
   * Call POST /agency/select with the chosen agency id, persist it as the
   * active agency, and refresh the auth payload (user info / sidebar /
   * permissions) so the app reflects the selected agency.
   */
  selectAgency: (agencyId: string) => Promise<void>;
  /**
   * Call POST /agency/switch with the chosen agency id, persist it as the
   * active agency, and refresh the auth payload. The previously active agency
   * is left intact when the request fails.
   */
  switchAgency: (agencyId: string) => Promise<void>;
  logout: () => void;
  getAuth: () => Promise<void>;
  login: (data: LoginRequest) => Promise<LoginResponse>;
}

export interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  profile_picture?: string;
  profile_picture_url_signed?: string;
  language: string;
  timezone: string;
  bio: string;
  user_type?: string;
}

export interface SidebarItem {
  id?: string | null;
  title: string;
  route: string;
  icon?: string | null;
  order?: number | null;
  visible?: boolean | null;
  permission?: string | null;
  children?: SidebarItem[] | null;
}

export interface LoginRequest {
  email: string;
  password: string;
}

/**
 * Shape of one entry returned by the backend's agency listing endpoints
 * (login multi-agency response, GET /agencies, etc).
 */
export interface AgencyMembership {
  agency_id: string;
  agency_name: string | null;
  role: string;
}

export interface LoginResponseData {
  user: User;
  token: string;
  /** Present on multi-agency logins. */
  agencies?: AgencyMembership[];
  /** Present on multi-agency logins. */
  requires_agency_selection?: boolean;
  /** Present on zero-agency logins. */
  requires_onboarding?: boolean;
  needs_onboarding?: boolean;
  /** Present on single-agency logins. */
  agency?: { id: string; name: string | null } | null;
  active_agency?: { id: string; name: string | null } | null;
}

export interface LoginResponse {
  data: LoginResponseData;
  message: string;
  success: boolean;
}

export interface UserResponse {
  user: User;
  roles: string[];
  permissions: string[];
  sidebar: SidebarItem[];
  media_agency_information: MediaAgencyInformation;
}

export interface MediaAgencyInformation {
  id: string;
  name: string;
  logo_url: string;
  logo_url_signed: string;
  bio: string | null;
  website: string;
  industry: string;
  size: string;
  phone: string;
  main_email: string;
  gst_id: string | null;
  agency_terms_accepted: boolean;
  agency_joined_at: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  billing_address: BillingAddress;
  recovery_email?: string;
  recovery_email_verified?: boolean;
  recovery_phone?: string;
  recovery_phone_verified?: boolean;
}

export interface BillingAddress {
  id: number;
  media_agency_information_id: string;
  address_line_1: string;
  address_line_2?: string | null;
  city: string;
  state: string;
  country: string;
  zip_postal: string;
  phone?: string | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

/**
 * Read the persisted active agency id on store creation so it rehydrates on
 * page reloads. Mirrors the existing localStorage pattern used for `authToken`.
 */
const readPersistedActiveAgencyId = (): string | null => {
  try {
    return localStorage.getItem(ACTIVE_AGENCY_STORAGE_KEY);
  } catch {
    return null;
  }
};

const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  user_permissions: null,
  user_role: null,
  sidebar: null,
  authToken: null,
  media_agency_information: null,
  activeAgencyId: readPersistedActiveAgencyId(),
  agencies: [],
  requiresAgencySelection: false,
  setUser: (user) => {
    set({ user });
  },
  setAuthToken: (token) => {
    localStorage.setItem("authToken", token);
    set({ authToken: token });
  },
  setAuth: (
    user,
    token,
    user_permissions,
    user_role,
    sidebar,
    media_agency_information
  ) => {
    localStorage.setItem("authToken", token);
    set({
      user,
      authToken: token,
      user_permissions,
      user_role,
      sidebar,
      media_agency_information,
    });
  },

  setMediaAgencyInformation: (media_agency_information) => {
    set({ media_agency_information });
  },

  setActiveAgency: (agencyId) => {
    if (agencyId) {
      try {
        localStorage.setItem(ACTIVE_AGENCY_STORAGE_KEY, agencyId);
      } catch {
        /* localStorage may be unavailable in private modes */
      }
    } else {
      try {
        localStorage.removeItem(ACTIVE_AGENCY_STORAGE_KEY);
      } catch {
        /* noop */
      }
    }
    set({ activeAgencyId: agencyId });
  },

  setAgencies: (agencies) => {
    set({ agencies });
  },

  loadAgencies: async () => {
    try {
      const response = await apiClient.get<{ data: AgencyMembership[] }>(
        "/agencies"
      );
      const agencies = response.data?.data ?? [];
      set({ agencies });
      return agencies;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const message = axiosError.response?.data?.message || "Failed to load agencies.";
      throw new Error(message);
    }
  },

  selectAgency: async (agencyId: string) => {
    try {
      await apiClient.post("/agency/select", { agency_id: agencyId });
      // Persist + apply the agency before refreshing user info so the
      // X-Agency-Id header is attached on the follow-up request.
      get().setActiveAgency(agencyId);
      set({ requiresAgencySelection: false });
      // Refresh user info / sidebar / permissions so they reflect the
      // selected agency.
      const refreshed = await apiClient.get<UserResponse>("/users/info");
      const data = refreshed.data;
      const token = get().authToken || localStorage.getItem("authToken") || "";
      get().setAuth(
        data.user,
        token,
        data.permissions,
        data.roles[0],
        data.sidebar,
        data.media_agency_information
      );
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const message = axiosError.response?.data?.message || "Failed to select agency.";
      throw new Error(message);
    }
  },

  switchAgency: async (agencyId: string) => {
    const previousAgencyId = get().activeAgencyId;
    try {
      await apiClient.post("/agency/switch", { agency_id: agencyId });
      // Update the active agency only after the backend confirms the switch
      // so a 403 leaves the previously active agency unchanged.
      get().setActiveAgency(agencyId);
      const refreshed = await apiClient.get<UserResponse>("/users/info");
      const data = refreshed.data;
      const token = get().authToken || localStorage.getItem("authToken") || "";
      get().setAuth(
        data.user,
        token,
        data.permissions,
        data.roles[0],
        data.sidebar,
        data.media_agency_information
      );
    } catch (error) {
      // Restore the previous active agency on failure (defensive: nothing was
      // overwritten, but be explicit).
      get().setActiveAgency(previousAgencyId);
      const axiosError = error as AxiosError<any>;
      const message = axiosError.response?.data?.message || "Failed to switch agency.";
      throw new Error(message);
    }
  },

  logout: () => {
    localStorage.removeItem("authToken");
    try {
      localStorage.removeItem(ACTIVE_AGENCY_STORAGE_KEY);
    } catch {
      /* noop */
    }
    set({
      user: null,
      authToken: null,
      activeAgencyId: null,
      agencies: [],
      requiresAgencySelection: false,
    });
  },

  getAuth: async () => {
    const token = localStorage.getItem("authToken");
    if (token) {
      if (
        get().user &&
        get().user_permissions &&
        get().user_role &&
        get().sidebar
      )
        return;

      try {
        const response = await apiClient.get<UserResponse>("/users/info");
        const data = response.data;
        get().setAuth(
          data.user,
          token,
          data.permissions,
          data.roles[0],
          data.sidebar,
          data.media_agency_information
        );
      } catch (error) {
        const axiosError = error as AxiosError<any>;
        const errorMessage = axiosError.response
          ? axiosError.response.data?.message || "Something went wrong"
          : "Something went wrong";
        get().logout();
        throw new Error(errorMessage);
      }
    } else {
      get().logout();
      throw new Error("Please log in again.");
    }
  },

  login: async (data: LoginRequest) => {
    try {
      const response = await apiClient.post<LoginResponse>("/login", data);
      const responseData = response.data.data;
      const { token } = responseData;
      get().setAuthToken(token);

      // Multi-agency: surface the selection requirement. Do NOT load user
      // info yet — the backend has not bound an agency, so /users/info would
      // resolve without an active agency and 403 on agency-scoped data.
      if (responseData.requires_agency_selection) {
        set({
          agencies: responseData.agencies ?? [],
          requiresAgencySelection: true,
        });
        // Clear any stale agency selection so the user must pick again.
        get().setActiveAgency(null);
        return response.data;
      }

      // Zero-agency / onboarding-required: leave active agency unset and let
      // the caller route to onboarding. Auth payload is intentionally not
      // refreshed here.
      if (responseData.requires_onboarding || responseData.needs_onboarding) {
        get().setActiveAgency(null);
        set({ agencies: [], requiresAgencySelection: false });
        return response.data;
      }

      // Single agency: bind the agency client-side so subsequent requests
      // carry the X-Agency-Id header, then load user info as before.
      const singleAgencyId = responseData.agency?.id ?? null;
      if (singleAgencyId) {
        get().setActiveAgency(singleAgencyId);
      }
      set({ requiresAgencySelection: false });

      const response1 = await apiClient.get<UserResponse>("/users/info");
      const userData = response1.data;
      get().setAuth(
        userData.user,
        token,
        userData.permissions,
        userData.roles[0],
        userData.sidebar,
        userData.media_agency_information
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },
}));

export default useAuthStore;
